import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Spec = {
  id: string;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
  rows: number;
  columns: number;
  rowLabelRule: string;
  columnLabelRule: string;
  positionOrder: string;
  isDefault: boolean;
  status: string;
};

type SpecForm = {
  code: string;
  nameZh?: string;
  nameEn?: string;
  rows: number;
  columns: number;
  rowLabelRule: string;
  columnLabelRule: string;
  positionOrder: string;
  isDefault?: boolean;
};

export function StorageBoxSpecsPage() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<Spec[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<SpecForm>();
  const [messageApi, holder] = message.useMessage();

  const title = (item: { nameZh: string | null; nameEn: string | null }) =>
    i18n.resolvedLanguage === 'en-US'
      ? (item.nameEn ?? item.nameZh)
      : (item.nameZh ?? item.nameEn);

  const load = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/storage-box-specs', {
        credentials: 'include',
      });
      if (!response.ok) throw new Error();
      setItems((await response.json()) as Spec[]);
    } catch {
      await messageApi.error(t('storage.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const create = () => {
    form.setFieldsValue({
      rows: 9,
      columns: 9,
      rowLabelRule: 'ALPHA',
      columnLabelRule: 'NUMERIC',
      positionOrder: 'ROW_MAJOR',
      isDefault: false,
    });
    setOpen(true);
  };

  const save = async (values: SpecForm) => {
    const response = await fetch('/api/v1/storage-box-specs', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(values),
    });
    if (!response.ok) {
      await messageApi.error(t('storage.saveFailed'));
      return;
    }
    setOpen(false);
    form.resetFields();
    await load();
    await messageApi.success(t('storage.saved'));
  };

  const remove = async (item: Spec) => {
    const response = await fetch(`/api/v1/storage-box-specs/${item.id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      await messageApi.error(
        response.status === 409
          ? t('storage.specificationDeleteReferenced')
          : t('storage.boxSpecDeleteFailed'),
      );
      return;
    }
    await load();
    await messageApi.success(t('storage.boxSpecDeleted'));
  };

  return (
    <section>
      {holder}
      <Card
        title={t('nav.boxSpecs')}
        extra={
          <Space>
            <Button type="link" icon={<PlusOutlined />} onClick={create}>
              {t('storage.createSpec')}
            </Button>
            <Tooltip title={t('storage.refresh')}>
              <Button
                aria-label={t('storage.refresh')}
                type="text"
                shape="circle"
                icon={<ReloadOutlined />}
                loading={loading}
                onClick={() => void load()}
              />
            </Tooltip>
          </Space>
        }
      >
        <Table
          rowKey="id"
          loading={loading}
          dataSource={items}
          tableLayout="fixed"
          scroll={{ x: 'max-content' }}
          pagination={false}
          columns={[
            { title: t('storage.code'), dataIndex: 'code' },
            { title: t('storage.name'), render: (_, row: Spec) => title(row) },
            {
              title: t('storage.dimensions'),
              render: (_, row: Spec) => `${row.rows} × ${row.columns}`,
            },
            { title: t('storage.rowLabel'), dataIndex: 'rowLabelRule' },
            { title: t('storage.columnLabel'), dataIndex: 'columnLabelRule' },
            { title: t('storage.positionOrder'), dataIndex: 'positionOrder' },
            {
              title: t('storage.default'),
              render: (_, row: Spec) =>
                row.isDefault ? (
                  <Tag color="blue">{t('storage.default')}</Tag>
                ) : null,
            },
            { title: t('storage.status'), dataIndex: 'status' },
            {
              title: t('storage.actions'),
              fixed: 'right' as const,
              width: 120,
              render: (_, row: Spec) => (
                <Popconfirm
                  title={t('storage.boxSpecDeleteConfirm', {
                    name: title(row) ?? row.code,
                  })}
                  okText={t('common.confirm')}
                  cancelText={t('common.cancel')}
                  onConfirm={() => void remove(row)}
                >
                  <Button danger type="link">
                    {t('common.delete')}
                  </Button>
                </Popconfirm>
              ),
            },
          ]}
        />
        <Modal
          title={t('storage.createSpec')}
          open={open}
          okText={t('common.save')}
          cancelText={t('common.cancel')}
          onCancel={() => setOpen(false)}
          onOk={() => void form.submit()}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => void save(values)}
          >
            <Form.Item
              name="code"
              label={t('storage.code')}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="nameZh"
              label={t('storage.nameZh')}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="nameEn" label={t('storage.nameEn')}>
              <Input />
            </Form.Item>
            <Form.Item
              name="rows"
              label={t('storage.rows')}
              rules={[{ required: true }]}
            >
              <InputNumber min={1} max={52} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="columns"
              label={t('storage.columns')}
              rules={[{ required: true }]}
            >
              <InputNumber min={1} max={200} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="rowLabelRule"
              label={t('storage.rowLabel')}
              rules={[{ required: true }]}
            >
              <Select
                options={['ALPHA', 'NUMERIC'].map((value) => ({
                  value,
                  label: value,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="columnLabelRule"
              label={t('storage.columnLabel')}
              rules={[{ required: true }]}
            >
              <Select
                options={['ALPHA', 'NUMERIC'].map((value) => ({
                  value,
                  label: value,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="positionOrder"
              label={t('storage.positionOrder')}
              rules={[{ required: true }]}
            >
              <Select
                options={['ROW_MAJOR', 'COLUMN_MAJOR'].map((value) => ({
                  value,
                  label: value,
                }))}
              />
            </Form.Item>
            <Form.Item name="isDefault" valuePropName="checked">
              <Checkbox>{t('storage.default')}</Checkbox>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </section>
  );
}
